import React, {
    useEffect,
    useState,
    useCallback, useRef,
} from "react";

import "../components/passwordNSecurity/MFAEnrollment.css";

import {userProfileService} from "../service/userProfile/userProfile.service";

import {
    ACCOUNT_SETTINGS_API_AUDIENCE,
    SENSITIVE_SCOPE,
} from "../config/securityContext";
import {ACR} from "../service/auth/ACR";
import {getStepUpMaxAge} from "../config/stepUpPolicy";
import DynamicAlert from "../helpers/dynamicAlert";
import {useLocation, useNavigate} from "react-router-dom";


const parseMfaId = (rawId) => {
    if (!rawId) return {};

    const [type, fullId] = rawId.split("|");

    return {
        methodType: type,
        methodId: fullId
            ? fullId.replace(/^dev_/, "")
            : null,
    };
};

const normalizeMfaMethods = (list = []) =>
    list.map((m) => ({
        ...m,
        ...parseMfaId(m.id),
    }));

export const PasswordAndSecurity = (props) => {
    const auth = props?.auth;
    const userEmail = props?.email;
    const location = useLocation();
    const navigate = useNavigate();

    const [methods, setMethods] = useState([]);

    const [loading, setLoading] = useState(true);

    const [resetLoading, setResetLoading] =
        useState(false);

    const [actionLoadingId, setActionLoadingId] =
        useState(null);

    const [alert, setAlert] = useState(null);

    const hasProcessedPending = useRef(false);

    const getType = (m) =>
        (m.methodType || m.type || m.method || m.factor_type || "").toLowerCase();

    const [pendingOperation, setPendingOperation] =
        useState(location?.state?.pendingOperation);

    const deleteMFAMethod = async () => {
        if (!pendingOperation) return;

        const scope = pendingOperation?.scope;

        if (scope !== SENSITIVE_SCOPE.UNENROLL_MFA) {
            return;
        }

        const {methodType, methodId} = pendingOperation;

        if (!methodType || !methodId) return;

        try {
            const res = await userProfileService.deleteMFAMethod(methodType, methodId, scope);

            if (res?.status === 200 || res?.success || res?.message) {
                setAlert({id: Date.now(), type: "success", msg: "Successfully deleted the MFA."});

            } else if (res?.error) {
                setAlert({id: Date.now(), type: "danger", msg: res.error});
            }
        } catch (error) {
            if (error?.status !== 404) {
                setAlert({
                    id: Date.now(),
                    type: "danger",
                    msg:
                        error?.message ||
                        "Unable to delete MFA method.",
                });
            }
        } finally {
            setPendingOperation(null);
            setActionLoadingId(null);
        }
    };

    const loadMFAMethods = async () => {

        try {
            await deleteMFAMethod();
            const res = await userProfileService.getMFAMethods();

            let data = Array.isArray(res?.data) ? normalizeMfaMethods(res.data) : [];

            const hasEmailMethod = data.some((m) => m.type?.toLowerCase() === "email" || m.method?.toLowerCase() === "email");

            if (!hasEmailMethod) {
                data = [{
                    id: "email-default",
                    type: "email",
                    method: "email",
                    email: userEmail,
                    value: userEmail,
                    isDefault: true,
                    last_auth_at: null,
                },
                    ...data,
                ];
            }

            setMethods(data);
        } catch (err) {
            console.error("Failed to load MFA methods:", err);

            setMethods([{
                    id: "email-default",
                    type: "email",
                    method: "email",
                    email: userEmail,
                    value: userEmail,
                    isDefault: true,
                },
            ]);

            setAlert({
                id: Date.now(),
                type: "danger",
                msg: "Failed to load MFA methods.",
            });
        }
    };

    useEffect(() => {
        const pending = location?.state?.pendingOperation || location?.state?.appState?.pendingOperation;

        if (!pending) return;

        if (hasProcessedPending.current) return;

        hasProcessedPending.current = true;

        setPendingOperation(pending);

        navigate(location.pathname, {replace: true, state: {},});
    }, [location, navigate]);

    useEffect(() => {
        if(!userEmail){
            return;
        }
        let isMounted = true;

        const init = async () => {
            setLoading(true);

            await loadMFAMethods();

            if (isMounted) {
                setLoading(false);
            }
        };

        init();

        return () => {isMounted = false;
        };
    }, [userEmail]);


    useEffect(() => {
        if (location.state?.action === "enroll-mfa") {

            setAlert({
                id: Date.now(),
                type: "success",
                msg: "MFA enrollment is successful.",
            });
        }
    }, [location.state?.action]);

    const handleResetPassword =
        useCallback(async () => {
            try {
                setResetLoading(true);

                await auth.loginWithRedirect({
                    authorizationParams: {
                        connection: "customers-db",
                        audience:
                        ACCOUNT_SETTINGS_API_AUDIENCE,
                        scope: `openid profile email ${SENSITIVE_SCOPE.CHANGE_PASSWORD}`,
                        acr_values: ACR.MFA,
                        login_hint: props?.email || "",
                        max_age:
                            getStepUpMaxAge(
                                SENSITIVE_SCOPE.CHANGE_PASSWORD
                            ),
                    },
                    appState: {
                        returnTo:
                            "/account-settings",
                        pendingOperation: {
                            type: "api_scope",
                            scope:
                            SENSITIVE_SCOPE.CHANGE_PASSWORD,
                        },
                    },
                });
            } catch (error) {
                console.error(
                    "Reset password error:",
                    error
                );

                setAlert({
                    id: Date.now(),
                    type: "danger",
                    msg: "Unable to start password reset.",
                });

                setResetLoading(false);
            }
        }, [auth, props?.email]);

    const handleDelete = useCallback(
        async (method) => {
            try {
                setActionLoadingId(method.id);

                await auth.loginWithRedirect({
                    authorizationParams: {
                        connection: "customers-db",
                        audience:
                        ACCOUNT_SETTINGS_API_AUDIENCE,
                        scope: `openid profile email ${SENSITIVE_SCOPE.UNENROLL_MFA}`,
                        acr_values: ACR.MFA,
                        login_hint:
                            props?.email || "",
                        max_age:
                            getStepUpMaxAge(
                                SENSITIVE_SCOPE.UNENROLL_MFA
                            ),
                    },

                    appState: {
                        returnTo:
                            "/account-settings",
                        pendingOperation: {
                            type: "api_scope",
                            scope:
                            SENSITIVE_SCOPE.UNENROLL_MFA,
                            methodType:
                            method.methodType,
                            methodId:
                            method.methodId,
                        },
                    },
                });
            } catch (err) {
                console.error(
                    "Delete MFA method error:",
                    err
                );

                setAlert({
                    id: Date.now(),
                    type: "danger",
                    msg: "Unable to delete MFA method.",
                });

                setActionLoadingId(null);
            }
        },
        [auth, props?.email]
    );

    const handleStartEnrollment = useCallback(
        async (e) => {
            e.preventDefault();

            try {
                await auth.loginWithRedirect({
                    authorizationParams: {
                        connection: "customers-db",
                        audience: ACCOUNT_SETTINGS_API_AUDIENCE,
                        scope: `openid profile email ${SENSITIVE_SCOPE.ENROLL_MFA}`,
                        acr_values: ACR.MFA,
                        login_hint: props?.email || "",
                        max_age: getStepUpMaxAge(
                            SENSITIVE_SCOPE.ENROLL_MFA
                        ),
                    },
                    appState: {
                        returnTo: "/account-settings",
                        pendingOperation: {
                            type: "api_scope",
                            scope: SENSITIVE_SCOPE.ENROLL_MFA,
                        },
                        action: "enroll-mfa",
                    },
                });
            } catch (error) {
                console.error(
                    "Start MFA enrollment error:",
                    error
                );

                setAlert({
                    id: Date.now(),
                    type: "danger",
                    msg: "Unable to start MFA enrollment.",
                });
            }
        },
        [auth, props?.email]
    );


    const formatLastUsed = (date) => {
        if (!date) return "Never";

        const diff =
            Date.now() -
            new Date(date).getTime();

        const minutes = Math.floor(
            diff / 60000
        );

        const hours = Math.floor(
            diff / 3600000
        );

        const days = Math.floor(
            diff / 86400000
        );

        if (minutes < 60) {
            return `${minutes} min ago`;
        }

        if (hours < 24) {
            return `${hours} hr ago`;
        }

        return `${days} day(s) ago`;
    };

    const getLabel = (m) => {
        const type = (m.methodType || m.type || m.method || "").toLowerCase();

        if (type === "phone" || type === "sms") {
            return "Sms";
        }

        if (type === "totp" || type === "authenticator") {
            return "Authenticator App";
        }

        if (type === "email") {
            return "Email";
        }

        return type;
    };

    if (loading) {
        return <div>Loading MFA info...</div>;
    }

    return (
        <div className="mfa-body">
            <div className="mfa-container">
                <div className="text-center m-6-auto">
                    <DynamicAlert alert={alert}/>
                    <div className="p-1 py-2">
                        <div className="section-title">
                            <h4 style={{margin: 0}}>
                                Password and Security
                            </h4>
                        </div>

                        <form onSubmit={(e) => e.preventDefault()} style={{margin: "3px", padding: 0, border: "none"}}>
                            <button
                                onClick={handleResetPassword}
                                type="button"
                                disabled={resetLoading}
                                style={{
                                    height: "30px",
                                    width: "50%",
                                    marginTop: "10px",
                                    marginBottom: "16px",
                                    borderRadius: "30px",
                                    backgroundColor: resetLoading ? "#8fc7a4" : "#007932",
                                    border: "none",
                                    color: "#ffffff",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    cursor: resetLoading ? "not-allowed" : "pointer",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                    opacity: resetLoading ? 0.8 : 1,
                                    padding: "0 24px",
                                }}
                            >
                                {resetLoading ? "Redirecting..." : "Reset Password"}
                            </button>
                        </form>

                        <form
                            onSubmit={(e) => e.preventDefault()}
                            style={{margin: 3, padding: 0, border: "none"}}
                        >
                            <div
                                className="mfa-section-heading"
                                style={{fontWeight: 500}}
                            >
                                Multi-Factor Authentication
                            </div>

                            {(() => {
                                const additionalMfaCount = methods.filter(
                                    (m) => m.type !== "email"
                                ).length;

                                return (
                                    <>
                                        {methods.length > 0 ? (
                                            methods.map((m) => {
                                                const isDefault = m.type === "email";
                                                const canDelete = additionalMfaCount > 1;

                                                return (
                                                    <div
                                                        key={m.id}
                                                        className="mfa-card"
                                                        style={{
                                                            margin: "8px 0",
                                                            padding: "12px",
                                                            border: "1px solid #eee",
                                                            borderRadius: "10px",
                                                            backgroundColor: "#fff"
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                justifyContent: "space-between"
                                                            }}
                                                        >
                                                            <strong>{getLabel(m)}</strong>

                                                            {isDefault && (
                                                                <span
                                                                    style={{
                                                                        fontSize: "12px",
                                                                        padding: "4px 10px",
                                                                        borderRadius: "20px",
                                                                        backgroundColor: "#e8f5ec",
                                                                        color: "#007932",
                                                                        border: "1px solid #b7dfc2",
                                                                        fontWeight: 600
                                                                    }}
                                                                >
                                            Default
                                        </span>
                                                            )}
                                                        </div>

                                                        <div
                                                            style={{
                                                                marginTop: "6px",
                                                                color: "#555"
                                                            }}
                                                        >
                                                            {m.email && <div>{m.email}</div>}
                                                            {m.phone_number && (
                                                                <div>
                                                                    Number: {m.phone_number}
                                                                </div>
                                                            )}
                                                        </div>

                                                        {getType(m) !== "email" && (
                                                            <div
                                                                className="mfa-last-used"
                                                                style={{
                                                                    marginTop: "4px",
                                                                    color: "#555"
                                                                }}
                                                            >
                                                                Last Used:{" "}
                                                                {m.last_auth_at
                                                                    ? formatLastUsed(
                                                                        m.last_auth_at
                                                                    )
                                                                    : "Never"}
                                                            </div>
                                                        )}

                                                        {!isDefault && (
                                                            <div style={{marginTop: "10px"}}>
                                                                <button
                                                                    type="button"
                                                                    disabled={
                                                                        actionLoadingId === m.id ||
                                                                        !canDelete
                                                                    }
                                                                    onClick={() =>
                                                                        canDelete &&
                                                                        handleDelete(m)
                                                                    }
                                                                    style={{
                                                                        width: "50%",
                                                                        height: "30px",
                                                                        borderRadius: "30px",
                                                                        border: "none",
                                                                        backgroundColor: canDelete
                                                                            ? "#8a0000"
                                                                            : "#c4c4c4",
                                                                        color: "#fff",
                                                                        fontSize: "14px",
                                                                        fontWeight: 600,
                                                                        cursor: canDelete
                                                                            ? "pointer"
                                                                            : "not-allowed",
                                                                        opacity:
                                                                            actionLoadingId === m.id
                                                                                ? 0.7
                                                                                : 1
                                                                    }}
                                                                >
                                                                    {actionLoadingId === m.id
                                                                        ? "Deleting..."
                                                                        : "Delete"}
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div style={{marginTop: 10}}>
                                                No MFA methods enrolled
                                            </div>
                                        )}

                                        {additionalMfaCount === 1 && (
                                            <div
                                                style={{
                                                    marginTop: "8px",
                                                    fontSize: "12px",
                                                    color: "#666",
                                                    fontStyle: "italic"
                                                }}
                                            >
                                                To delete this MFA, you must first enroll an additional factor.
                                            </div>
                                        )}
                                    </>
                                );
                            })()}
                        </form>
                        <form onSubmit={handleStartEnrollment} style={{margin: "3px", padding: 0, border: "none"}}>
                            <div
                                className="mfa-section-heading"
                                style={{fontWeight: 500}}
                            >
                                Enroll Additional Factors
                            </div>


                            <button
                                className="mfa-button"
                                type="submit"
                                disabled={resetLoading}
                                style={{
                                    width: "50%",
                                    height: "30px",
                                    marginTop: "10px",
                                    marginBottom: "0px",
                                    borderRadius: "30px",
                                    backgroundColor: resetLoading
                                        ? "#8fc7a4"
                                        : "#007932",
                                    border: "none",
                                    color: "#ffffff",
                                    fontSize: "16px",
                                    fontWeight: "600",
                                    cursor: resetLoading
                                        ? "not-allowed"
                                        : "pointer",
                                    transition: "all 0.2s ease",
                                    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
                                    opacity: resetLoading ? 0.8 : 1,
                                    padding: "0 24px",
                                }}
                            >
                                Start Enrollment
                            </button>
                        </form>

                    </div>
                </div>
            </div>
        </div>
    );
};